'use client';

/**
 * Host adapter contract for the {@code NotificationsBell} widget.
 *
 * <p>Ported chrome must not hardcode backend URLs — different hosts
 * have different notification endpoints, payload shapes, and auth
 * mechanisms. The widget reads via {@link useNotificationsSource}
 * from a context the host provides via
 * {@link NotificationsSourceProvider}.
 *
 * <h3>Returns null when no source is mounted</h3>
 * NotificationsBell renders nothing (no bell icon at all) when the
 * source is missing. Hosts that don't have notifications wired up
 * see a chrome without a bell — preferable to showing a bell that
 * does nothing on click. The mount decision belongs to the host.
 */
import { createContext, useContext, type ReactNode } from 'react';

/** Fired by a host's event-bus listener whenever a push message arrives
 *  that corresponds to a newly created notification (any type — points,
 *  badges, comments, approvals, ...), so {@code NotificationsBell} can
 *  refresh its unread count / list immediately instead of waiting for
 *  the next poll tick. */
export const NOTIFICATIONS_REFRESH_EVENT = 'akademi:notifications-refresh';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  /** Free-form kind id — drives the per-row icon in
   *  {@code NotificationsBell}. Known values used by the default
   *  icon mapping: {@code course_update}, {@code achievement},
   *  {@code announcement}, {@code reminder}. Unknown kinds fall back
   *  to the generic bell icon. */
  kind: string;
  /** Optional click-through link. When absent, the row activates
   *  (marks-read) but doesn't navigate. */
  href?: string;
  /** Optional icon/avatar URL to display instead of the kind-based
   *  inline SVG (e.g. user avatar, badge image). */
  iconUrl?: string;
  /** Optional rich-text label component. When provided, rendered
   *  instead of the plain {@code body} string in the bell dropdown.
   *  Defined by the host adapter so it can use host-side hooks
   *  (i18n, Redux, etc.) without importing them into this package. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label?: React.ComponentType<any>;
  /** ISO 8601 timestamp. The widget computes relative time and
   *  buckets (today / this week / earlier) from this. */
  createdAt: string;
  read: boolean;
}

export interface NotificationsPage {
  items: NotificationItem[];
  /** Total unread count across all pages. Optional — widget falls
   *  back to counting unread items in the current page when absent. */
  unreadCount?: number;
  /** Opaque cursor for the next page. {@code null} = no more pages. */
  nextCursor?: string | null;
}

export interface NotificationsSource {
  /** Fetch a page of notifications. The widget calls this on first
   *  open (cursor undefined) and on "Load more" (cursor set). */
  fetchPage(opts?: {
    cursor?: string | null;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<NotificationsPage>;

  /** Cheap endpoint for the badge — polled every 60s while the tab
   *  is visible. Hosts that lack a dedicated unread-count endpoint
   *  can implement this by calling {@link fetchPage} and summing. */
  fetchUnreadCount(signal?: AbortSignal): Promise<number>;

  /** Optimistic mark-read. Widget updates UI first; failure is
   *  silent (best-effort) — same behaviour as ThemeMaker. */
  markRead(id: string): Promise<void>;

  /** Optimistic mark-all-read. */
  markAllRead(): Promise<void>;
}

const NotificationsSourceContext = createContext<NotificationsSource | null>(null);

export function NotificationsSourceProvider({
  value,
  children,
}: {
  value: NotificationsSource;
  children: ReactNode;
}) {
  return (
    <NotificationsSourceContext.Provider value={value}>
      {children}
    </NotificationsSourceContext.Provider>
  );
}

/** Returns the host-provided source, or {@code null} when no provider
 *  is mounted. {@code NotificationsBell} renders nothing in that case. */
export function useNotificationsSource(): NotificationsSource | null {
  return useContext(NotificationsSourceContext);
}
